import { Outlet, Link, useLocation, NavLink } from "react-router-dom";
import { Heart, Menu, X, Moon, Sun, ChevronRight, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { NAV, ASSETS } from "../mock";
import { useTheme } from "../context/ThemeContext";
import { getPlatformMeta } from "../socials";
import axios from "axios";
import { API } from "../context/AuthContext";

export default function Layout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      {/* Sticky Header */}
      <header
        className={`sticky top-0 z-40 header-shrink pf-gradient ${scrolled ? "is-shrunk" : ""}`}
        style={{ paddingTop: scrolled ? undefined : "1.25rem", paddingBottom: scrolled ? undefined : "0.75rem" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="oval-shrink flex items-center gap-3 sm:gap-4 group" aria-label="Ir al inicio">
              <img
                src={ASSETS.logo}
                alt="Logo Pequeño Fresno"
                className="h-16 sm:h-20 w-auto drop-shadow-sm transition-transform group-hover:scale-105 shrink-0"
              />
              <div className="pf-oval relative px-5 py-2.5 sm:px-7 sm:py-3 shadow-[0_10px_30px_-15px_rgba(77,106,58,0.35)]">
                <h1 className="font-cursive leading-tight text-[#7a8e5a] text-xl sm:text-3xl whitespace-nowrap">
                  asociación pequeño fresno
                </h1>
                {!scrolled && (
                  <p className="text-[10px] sm:text-xs text-[#6b7d51] italic mt-0.5">
                    Informando sobre el C.E.T.
                  </p>
                )}
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full text-sm transition-all ${
                      isActive
                        ? "bg-white/70 text-[#3d5a2a] font-semibold shadow-sm"
                        : "text-[#3d4a30] hover:bg-white/40"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={toggle}
                className="ml-2 p-2 rounded-full bg-white/40 hover:bg-white/70 transition"
                title={theme === "dark" ? "Modo claro" : "Modo oscuro"}
                aria-label="Cambiar tema"
              >
                {theme === "dark" ? <Sun className="w-4 h-4 text-[#3d4a30]" /> : <Moon className="w-4 h-4 text-[#3d4a30]" />}
              </button>
              <a
                href="https://donorbox.org/ayudanos-934402"
                target="_blank"
                rel="noreferrer"
                className="ml-2 btn-accent inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold"
              >
                <Heart className="w-4 h-4" /> Donar
              </a>
            </nav>

            <div className="lg:hidden flex items-center gap-2">
              <button onClick={toggle} className="p-2 rounded-full bg-white/60" aria-label="Tema">
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button className="p-2 rounded-full bg-white/60 backdrop-blur" onClick={() => setOpen(true)} aria-label="Menú">
                <Menu className="w-6 h-6 text-[#3d4a30]" />
              </button>
            </div>
          </div>
        </div>

        {!scrolled && !isHome && (
          <>
            <img
              src={ASSETS.butterflyLeaf}
              alt=""
              className="hidden md:block absolute right-4 top-2 w-48 lg:w-56 pointer-events-none animate-floaty leaf-shadow opacity-90"
            />
            <img
              src={ASSETS.smallLeaf}
              alt=""
              aria-hidden="true"
              className="hidden md:block absolute right-52 lg:right-60 top-4 w-14 pointer-events-none opacity-75 rotate-[20deg] leaf-shadow"
            />
          </>
        )}
      </header>

      {/* Breadcrumbs */}
      {!isHome && <Breadcrumbs />}

      {/* Mobile Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[#3d4a30]/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-72 shadow-xl p-6" style={{ background: "var(--bg-soft)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <button onClick={() => setOpen(false)} className="p-2"><X className="w-6 h-6" /></button>
            </div>
            <ul className="mt-6 space-y-2">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} onClick={() => setOpen(false)} className="block px-4 py-2 rounded-lg hover:bg-white/60 text-[color:var(--text)] font-medium">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <a href="https://donorbox.org/ayudanos-934402" target="_blank" rel="noreferrer" className="block px-4 py-2 rounded-lg btn-accent text-center font-semibold">
                  Donar
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  const labels = {
    cet: "C.E.T.",
    asociacion: "Asociación",
    eventos: "Eventos",
    nosotros: "Acerca de",
    contacto: "Contacto",
    login: "Acceso",
    admin: "Panel Admin",
  };
  return (
    <nav aria-label="breadcrumb" className="max-w-7xl mx-auto px-6 pt-4 text-sm">
      <ol className="flex items-center gap-1.5 text-[color:var(--text-soft)]">
        <li><Link to="/" className="hover:text-[color:var(--brand)]">Inicio</Link></li>
        {parts.map((p, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
            <span className={i === parts.length - 1 ? "text-[color:var(--brand)] font-semibold" : ""}>
              {labels[p] || p}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [socials, setSocials] = useState([]);

  const loadSocials = async () => {
    try {
      const { data } = await axios.get(`${API}/socials`);
      setSocials(data.filter((s) => s.enabled && s.url));
    } catch {
      setSocials([]);
    }
  };

  useEffect(() => {
    loadSocials();
    const handler = () => loadSocials();
    window.addEventListener("pf:socials:refresh", handler);
    return () => window.removeEventListener("pf:socials:refresh", handler);
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setStatus("...");
    try {
      await axios.post(`${API}/messages`, {
        name: "Suscripción Newsletter",
        email,
        subject: "Newsletter",
        message: `Nueva suscripción a newsletter: ${email}`,
      });
      setStatus("¡Gracias! Te avisaremos.");
      setEmail("");
    } catch {
      setStatus("Error, intenta más tarde.");
    }
  };

  return (
    <footer className="mt-24 pf-gradient">
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <h3 className="font-cursive text-3xl text-[color:var(--brand)]">Pequeño Fresno</h3>
          <p className="mt-3 text-sm text-[color:var(--text)] leading-relaxed">
            Asociación sin ánimo de lucro dedicada a informar sobre el
            Complejo de Esclerosis Tuberosa y apoyar a las familias.
          </p>
          {socials.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {socials.map((s) => {
                const meta = getPlatformMeta(s.platform);
                const Icon = meta.icon;
                return (
                  <a
                    key={s.id || s.url}
                    href={s.url}
                    target={s.url.startsWith("mailto:") ? undefined : "_blank"}
                    rel="noreferrer"
                    className="p-2 rounded-full bg-white/60 hover:bg-white transition"
                    aria-label={s.label || meta.label}
                    title={s.label || meta.label}
                  >
                    <Icon className="w-4 h-4 text-[color:var(--brand)]" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h4 className="h-serif font-semibold text-[color:var(--brand)] mb-3">Enlaces</h4>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-[color:var(--text)] hover:text-[color:var(--brand)]">{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="h-serif font-semibold text-[color:var(--brand)] mb-3">Legal</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/aviso-legal" className="text-[color:var(--text)] hover:text-[color:var(--brand)]">Aviso legal</Link></li>
            <li><Link to="/privacidad" className="text-[color:var(--text)] hover:text-[color:var(--brand)]">Política de privacidad</Link></li>
            <li><span className="text-[color:var(--text-soft)]">CIF: CV-01-066518-A</span></li>
          </ul>
        </div>

        <div>
          <h4 className="h-serif font-semibold text-[color:var(--brand)] mb-3">Newsletter</h4>
          <p className="text-sm text-[color:var(--text)] mb-3">Recibe nuestras novedades y eventos.</p>
          <form onSubmit={subscribe} className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="flex-1 px-3 py-2 rounded-lg border border-white/60 bg-white/70 text-sm text-[#3d4a30] focus:outline-none focus:ring-2 focus:ring-[#4d6a3a]/40"
            />
            <button type="submit" className="btn-brand px-3 py-2 rounded-lg" aria-label="Suscribir">
              <Send className="w-4 h-4" />
            </button>
          </form>
          {status && <p className="text-xs mt-2 text-[color:var(--text-soft)]">{status}</p>}
          <a
            href="https://donorbox.org/ayudanos-934402"
            target="_blank"
            rel="noreferrer"
            className="mt-5 btn-accent inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            <Heart className="w-4 h-4" /> Hacer donación
          </a>
        </div>
      </div>
      <div className="border-t border-white/30">
        <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-[color:var(--text-soft)] flex flex-wrap justify-between gap-3">
          <span>© {new Date().getFullYear()} Asociación Pequeño Fresno · Alicante</span>
          <span>Hecho con 💚 para las pequeñas mariposas</span>
        </div>
      </div>
    </footer>
  );
}
